import React, { useContext, FC, MouseEvent, FocusEvent } from 'react';

import isArray from 'lodash/isArray';
import { mqMin, LucidTheme } from '@lucid/theme';
import { getLinkPath, isAbsolutePath, DrupalUrl } from '@lucid/utils';

import Box from '../box/box';
import Heading from '../heading/heading';
import ImageWithPlaceholder from  '../image/image-with-placeholder';
import LayoutContext from '../context/layout-context';
import Link from '../link/link';
import NavLink from './nav-link';
import Nav from './nav';
import { NavMenuIconOptions } from './nav-menu-icon';
import Text from '../text/text';
import { LinkInterface } from '../../types/types';

export interface NavDropdownMenuProps extends NavMenuIconOptions {
  /** Allows the component to get additional styles. */
  className?: string;
  /** Checks if the dropdown should be visible or hidden. */
  isDropdownOpen?: boolean;
  /** The link and nested links to be shown in the dropdown menu. */
  link: LinkInterface;
  onBlur?: (event: FocusEvent) => void;
  onFocus?: (event: FocusEvent) => void;
  onMouseEnter?: (event: MouseEvent) => void;
  onMouseLeave?: (event: MouseEvent) => void;
}

type NavDropdownLinkGroups = (LinkInterface[] | null | undefined)[];

export interface NavDropdownMenuLinkInterface extends LinkInterface {
  /** Creates proper size groups for nested links to be shown in the dropdown menu. */
  linkGroups?: NavDropdownLinkGroups;
  /** Hides the group title if the link group is split into 2 or more groups. */
  shouldHideGroupTitle?: boolean;
}

const chunkLinks = (link: LinkInterface, links: LinkInterface[] = [], columnLength?: number) => {
  // set the perfered column length for the dropdown based on the parent if provided, otherwise fallback to default of 4 per column.
  const parentColumnLength = columnLength || 4;
  // set the final column length to the group/category header value if provided, otherwise fallback to parent value.
  const finalColumnLength = link.fieldCustomColumnLength || parentColumnLength;
  return links.map(
    (_, i) => i % finalColumnLength === 0
      ? link.links?.slice(i, i + finalColumnLength)
      : null
  ).filter(Boolean);
};

const formatLinkGroups = (link: LinkInterface) => {
  const currentMenu = link.links?.reduce<NavDropdownMenuLinkInterface[]>((previous, item) => {
    // if the item has links (children) it means this item is a category and the children need to be chunked together into groups
    if (item.links && !!item.links.length) {
      const currentItems = chunkLinks(item, item.links, link.fieldCustomColumnLength);
      return [
        ...previous,
        {
          ...item,
          linkGroups: currentItems || []
        }
      ];
    }
    // if the item has no links (children) it means it is part of a top level group and it needs to be chunked together with its siblings
    const currentItems = chunkLinks(link, link.links, link.fieldCustomColumnLength);
    const hasPreviousLink = previous.find(pItem => pItem.entityId === link.entityId);
    if (!hasPreviousLink) {
      const currentLinkGroupItems: NavDropdownMenuLinkInterface[] = currentItems.map(menuGroup => {
        if (menuGroup && !!menuGroup.length) {
          return ({
            ...link,
            shouldHideGroupTitle: true,
            linkGroups: [...menuGroup]
          });
        }

        return {};
      });

      return [
        ...previous,
        ...currentLinkGroupItems
      ];
    }

    return previous;
  }, []);

  return currentMenu;
};

const renderDropdownLink = ({
  link,
  index,
  isTopNav,
  hasFeaturedItem
}: {
  link: LinkInterface,
  index: number,
  isTopNav?: boolean,
  hasFeaturedItem?: boolean
}) => {
  const isProductSuite = link.entityBundle === 'product-suite';
  const isTopNavigation = link.entityBundle === 'top-navigation';

  if ((isProductSuite || isTopNavigation) && link.isEnabled === false) {
    // use '=== false' instead of '!link.enabled'
    // since this is used across all sites, err on the side of rendering the item
    // in case "enabled" is removed from the query
    return null;
  }

  return (
    <Box
      key={`navDropdownMenuLink-${link.entityId}-${index + 1}`}
      css={[
        {
          label: 'navDropdownMenuLinkContainer',
          display: 'flex',
          [mqMin.tabletSmall]: {
            flex: '0 1 100%',
            maxWidth: '100%'
          },
          [mqMin.tablet]: {
            flex: '0 1 95%',
            maxWidth: '95%'
          }
        },
        !hasFeaturedItem && {
          [mqMin.tabletSmall]: {
            flex: '0 1 48%',
            maxWidth: '48%'
          },
          [mqMin.tablet]: {
            flex: '0 1 95%',
            maxWidth: '95%'
          }
        }
      ]}
    >
      <NavLink
        isInDropdown
        isTopNav={isTopNav}
        link={link}
        variant="subNavLink"
      />
    </Box>
  );
};

const renderDropdownLinkGroup = (
  {
    link,
    isGroupSplit,
    index,
    isTopNav,
    hasFeaturedItem,
    viewMoreLink,
    viewMoreText
  }: {
    link: NavDropdownMenuLinkInterface,
    isGroupSplit?: boolean,
    index: number,
    isTopNav?: boolean,
    hasFeaturedItem?: boolean,
    viewMoreLink?: string,
    viewMoreText?: string
  }
) => (
  <Box
    key={`navDropdownMenuLinksGroup-${link.entityId}-${index + 1}`}
    css={(theme: LucidTheme) => [
      {
        label: 'navDropdownMenuLinksGroup',
        justifyContent: 'space-between',
        [mqMin.tabletSmall]: {
          flex: '0 1 100%',
          maxWidth: '100%'
        },
        [mqMin.tabletLandscape]: {
          flex: '0 1 50%',
          maxWidth: '50%',
          display: 'block'
        }
      },
      isGroupSplit && {
        [mqMin.tabletLandscape]: {
          marginTop: theme.space.s24
        }
      },
      !hasFeaturedItem && {
        [mqMin.tabletSmall]: {
          display: 'flex',
          flexWrap: 'wrap',
          flex: '0 1 100%',
          maxWidth: '100%'
        },
        [mqMin.tabletLandscape]: {
          flex: '0 1 50%',
          maxWidth: '50%',
          display: 'block'
        }
      }
    ]}
    data-testid="dropdown-group"
  >
    {!link.shouldHideGroupTitle && (
      <Text
        as="h2"
        css={(theme: LucidTheme) => [
          {
            marginTop: 0,
            paddingTop: theme.space.s16,
            [mqMin.tabletLandscape]: {
              paddingTop: 0,
              '& > a': {
                paddingTop: 0
              }
            }
          },
          !hasFeaturedItem && {
            [mqMin.tabletSmall]: {
              flex: '0 1 100%',
              maxWidth: '100%'
            }
          }
        ]}
        dataElementPageLocation={isTopNav ? 'topNav' : 'siteNav'}
        variant="interactiveXS400"
      >
        {link.title}
      </Text>
    )}
    {link.linkGroups?.map(itemLinkGroups => {
      if (itemLinkGroups && !!itemLinkGroups.length) {
        return itemLinkGroups?.map((subItemLink) => renderDropdownLink({
          link: subItemLink,
          index,
          isTopNav,
          hasFeaturedItem
        }));
      }

      const itemLinkGroup = itemLinkGroups as LinkInterface;
      return renderDropdownLink({
        link: itemLinkGroup,
        index,
        isTopNav,
        hasFeaturedItem
      });
    }
    )}
    {viewMoreLink && (
      <Box
        css={[
          {
            label: 'navDropdownMenuLinkContainer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            width: '100%',
            [mqMin.tabletSmall]: {
              flex: '0 1 100%',
              maxWidth: '100%'
            },
            [mqMin.tablet]: {
              flex: '0 1 95%',
              maxWidth: '95%'
            }
          },
          !hasFeaturedItem && {
            [mqMin.tabletSmall]: {
              flex: '0 1 48%',
              maxWidth: '48%'
            },
            [mqMin.tablet]: {
              flex: '0 1 95%',
              maxWidth: '95%'
            }
          }
        ]}
      >
        {/* NOTE: this extra box is used to push the link to the bottom */}
        <Box />
        <Box
          css={(theme: LucidTheme) => ({
            margin: theme.space.s16,
            marginRight: theme.space.s24
          })}
        >
          <Link hasArrow to={viewMoreLink}>{viewMoreText}</Link>
        </Box>
      </Box>
    )}
  </Box>
);

const NavDropdownMenu: FC<NavDropdownMenuProps> = (
  {
    className,
    isDropdownOpen,
    isTopNav = false,
    link,
    onBlur,
    onFocus,
    onMouseEnter,
    onMouseLeave,
    ...rest
  }
) => {
  const { language: { shortLangPrefix, langPrefix } } = useContext(LayoutContext);
  const { isDropdownExpanded, viewMoreText } = useContext(Nav.Context);
  const currentMenu = formatLinkGroups(link);
  const featuredItem = link.fieldDropdownFeaturedItem?.entity.entityTranslations?.find(
    currentFeaturedItem => currentFeaturedItem.langcode.value === shortLangPrefix
  );
  const hasFeaturedItem = typeof featuredItem !== 'undefined';
  const formatFeaturedItemLink = (link: DrupalUrl) => {
    const currentLink = getLinkPath(link, langPrefix);

    if (isTopNav && currentLink && !isAbsolutePath(currentLink)) {
      return `https://lucid.co${currentLink}`;
    }

    return currentLink;
  };

  const viewMoreLink =  getLinkPath(link?.fieldViewMore?.url as DrupalUrl, langPrefix);

  const handleOnMouseEnter = (e: MouseEvent) => {
    if (onMouseEnter) {
      return onMouseEnter(e);
    }
  };

  const handleOnMouseLeave = (e: MouseEvent) => {
    if (onMouseLeave) {
      return onMouseLeave(e);
    }
  };

  const handleOnBlur = (e: FocusEvent) => {
    if (onBlur) {
      return onBlur(e);
    }
  };

  const handleOnFocus = (e: FocusEvent) => {
    if (onFocus) {
      return onFocus(e);
    }
  };

  return (
    <Box
      {...rest}
      aria-hidden={!isDropdownOpen}
      css={(theme: LucidTheme) => [
        {
          label: 'navDropdownMenu',
          display: 'flex',
          flexDirection: 'column',
          flexWrap: 'nowrap',
          backgroundColor: theme.colors.white,
          zIndex: theme.zIndices.zNegative1,
          transition: `max-height 0.5s ease,
          margin-top 0.5s step-end,
          margin-bottom 0.5s step-end,
          border-top 0.5s ease,
          box-shadow 0.5s ease,
          padding-bottom 0.5s linear`,
          position: 'relative',
          top: 0,
          right: 0,
          left: 0,
          height: '100%',
          maxHeight: '0px',
          width: '100%',
          maxWidth: '100%',
          paddingLeft: theme.space.s32,
          paddingRight: theme.space.s32,
          marginTop: `-${theme.space.s32}px`,
          marginBottom: theme.space.s32,
          overflow: 'hidden',
          [mqMin.tabletSmall]: {
            flexDirection: 'row',
            flexWrap: 'wrap'
          },
          [mqMin.tabletLandscape]: {
            transform: 'translateY(calc(-105% - 140px))',
            transition: 'z-index 0.5s step-start',
            // transition: 'z-index 0.5s step-start, transform 0.5s ease, max-height 0.5s ease-in-out',
            position: 'absolute',
            top: 70,
            padding: 0,
            flexDirection: 'row',
            flexWrap: 'nowrap',
            borderStyle: 'solid',
            borderColor: 'coolGrey.10',
            borderWidth: '1px',
            borderRadius: theme.radii.base,
            height: 'auto',
            width: 'calc(100% - (3.75rem * 2))',
            margin: '0 3.75rem'
          },
          [mqMin.maxWidth]: {
            width: 'calc(100% - 15rem)',
            margin: '0 7.5rem'
          }
        },
        isDropdownExpanded && {
          // [mqMin.tabletLandscape]: {
          //   transform: 'translateY(0)'
          // }
        },
        isDropdownOpen && {
          zIndex: theme.zIndices.z100,
          transition: `max-height 0.5s ease,
          margin-top 0.5s step-start,
          margin-bottom 0.5s step-start,
          border-bottom 0.5s ease,
          box-shadow 0.5s ease,
          padding-bottom 0.5s linear`,
          borderBottom: `1px solid ${theme.colors.coolGrey[40]}`,
          boxShadow: theme.shadows.nav,
          maxHeight: '1400px',
          marginTop: theme.space.s16,
          marginBottom: 0,
          paddingBottom: theme.space.s32,
          [mqMin.tabletLandscape]: {
            zIndex: theme.zIndices.z100,
            transform: 'translateY(0)',
            transition: 'z-index 0.5s step-end',
            // transition: 'z-index 0.5s step-end, transform 0.5s ease-in-out, max-height 0.5s ease-in-out',
            marginTop: 'unset',
            paddingBottom: 'unset'
          }
        }
      ]}
      // eslint-disable-next-line react/jsx-sort-props
      className={className}
      onBlur={handleOnBlur}
      onFocus={handleOnFocus}
      onMouseEnter={handleOnMouseEnter}
      onMouseLeave={handleOnMouseLeave}
    >
      <Box
        css={(theme: LucidTheme) => ({
          label: 'navDropdownMenuTextContent',
          display: 'none',
          visibility: 'hidden',
          position: 'relative',
          [mqMin.tabletLandscape]: {
            display: 'flex',
            visibility: 'visible',
            flexDirection: 'column',
            flex: '0 1 25%',
            maxWidth: '25%',
            background: theme.colors.coolGrey[100],
            padding: `${theme.space.s48}px ${theme.space.s40}px`,
            color: theme.colors.white,
            borderTopLeftRadius: '3px',
            borderBottomLeftRadius: '3px'
          }
        })}
      >
        <Heading
          as="h2"
          color="white"
          dataElementPageLocation={isTopNav ? 'topNav' : 'siteNav'}
          mb="3"
          variant="headlineL"
        >
          {link.title}
        </Heading>
        <Text
          as="span"
          css={(theme: LucidTheme) => ({
            fontSize: theme.fontSizes[4],
            color: theme.colors.coolGrey[0],
            fontWeight: theme.fontWeights.light
          })}
          dataElementPageLocation={isTopNav ? 'topNav' : 'siteNav'}
        >
          {link.fieldDropdownDescription}
        </Text>
      </Box>
      <Box
        css={(theme: LucidTheme) => [
          {
            label: 'navDropdownMenuLinks',
            position: 'relative',
            alignSelf: 'flex-start',
            [mqMin.tabletSmall]: {
              width: '100%'
            },
            [mqMin.tabletLandscape]: {
              display: 'flex',
              flex: '0 1 50%',
              maxWidth: '50%',
              padding: `${theme.space.s24}px 0 ${theme.space.s24}px ${theme.space.s24}px`
            }
          },
          hasFeaturedItem && {
            [mqMin.tabletSmall]: {
              flex: '0 1 48%',
              maxWidth: '48%'
            }
          },
          !hasFeaturedItem && {
            [mqMin.tabletLandscape]: {
              flex: '0 1 75%',
              maxWidth: '75%',
              padding: theme.space.s24
            }
          },
          isDropdownOpen && {
            zIndex: theme.zIndices.z500
          }
        ]}
      >
        {currentMenu?.map(
          (itemLink, itemLinkIndex, menuArr) => {
            const isNestedLinkGroups = itemLink.linkGroups
            && !!itemLink.linkGroups.length
            && itemLink.linkGroups.length > 1
            && itemLink.linkGroups.map(linkGroup => isArray(linkGroup)).some(Boolean);

            const isLastColumn = menuArr.length === itemLinkIndex + 1;
            const shouldShowViewMore = isLastColumn && !!viewMoreLink;

            if (isNestedLinkGroups) {
              return itemLink.linkGroups?.map(
                (linkGroup, linkGroupIndex) => {
                  const isGroupSplit = linkGroupIndex !== 0;
                  const nestedLink = {
                    ...itemLink,
                    linkGroups: linkGroup,
                    shouldHideGroupTitle: isGroupSplit
                  } as NavDropdownMenuLinkInterface;

                  return renderDropdownLinkGroup({
                    link: nestedLink,
                    isGroupSplit,
                    index: linkGroupIndex,
                    isTopNav,
                    hasFeaturedItem,
                    viewMoreLink: shouldShowViewMore ? viewMoreLink : undefined,
                    viewMoreText
                  });
                });
            }

            return renderDropdownLinkGroup({
              link: itemLink,
              index: itemLinkIndex,
              isTopNav,
              hasFeaturedItem,
              viewMoreLink: shouldShowViewMore ? viewMoreLink : undefined,
              viewMoreText
            });
          }
        )}
      </Box>
      {hasFeaturedItem && featuredItem?.fieldResourceLink?.url && (
        <Box
          css={(theme: LucidTheme) => [
            {
              label: 'navDropdownMenuFeaturedItem',
              position: 'relative',
              alignSelf: 'center',
              paddingTop: theme.space.s32,
              [mqMin.tabletSmall]: {
                flex: '0 1 48%',
                maxWidth: '48%',
                alignSelf: 'flex-start'
              },
              [mqMin.tabletLandscape]: {
                flex: '0 1 25%',
                maxWidth: '25%',
                paddingTop: 'unset'
              }
            },
            isDropdownOpen && {
              zIndex: theme.zIndices.z500
            }
          ]}
        >
          <Link
            css={(theme: LucidTheme) => [
              {
                '&:hover': {
                  backgroundColor: theme.colors.coolGrey[5],
                  borderRadius: theme.radii.base
                },
                [mqMin.tabletLandscape]: {
                  margin: `${theme.space.s48}px ${theme.space.s40}px ${theme.space.s48}px 0`
                }
              },
              isTopNav && {
                '&:active, &:focus': {
                  color: theme.colors.blue[60],
                  '& > span, & > p': {
                    color: theme.colors.blue[60]
                  }
                },
                '&:hover': {
                  color: theme.colors.blue[60],
                  '& > span, & > p': {
                    color: theme.colors.blue[60]
                  }
                },
                '& > p': {
                  '&:hover': {
                    color: theme.colors.blue[60]
                  },
                  '&:active, &:focus': {
                    color: theme.colors.blue[60]
                  }
                }
              }
            ]}
            tabIndex={isDropdownOpen ? 0 : -1}
            to={formatFeaturedItemLink(featuredItem.fieldResourceLink.url)}
            variant="subNavLink"
          >
            {(featuredItem.fieldImageUrl || featuredItem.fieldImage) && (
              <ImageWithPlaceholder
                alt={featuredItem.fieldAlt || 'Featured Resource'}
                aspectRatio="4:3"
                css={(theme: LucidTheme) => ({
                  marginBottom: theme.space.s16
                })}
                dataElementPageLocation={isTopNav ? 'topNav' : 'siteNav'}
                src={featuredItem.fieldImageUrl || featuredItem.fieldImage || ''}
              />
            )}
            {featuredItem.fieldTitle && (
              <Text
                css={(theme: LucidTheme) => ({
                  margin: `${theme.space[2]}px 0`
                })}
                dataElementPageLocation={isTopNav ? 'topNav' : 'siteNav'}
                variant="interactiveS700"
              >
                {featuredItem.fieldTitle}
              </Text>
            )}
            {featuredItem.fieldSubtitle && (
              <Text
                css={(theme: LucidTheme) => ({
                  lineHeight: theme.lineHeights.body
                })}
                dataElementPageLocation={isTopNav ? 'topNav' : 'siteNav'}
                variant="interactiveXS400"
              >
                {featuredItem.fieldSubtitle}
              </Text>
            )}
          </Link>
        </Box>
      )}
    </Box>
  );
};

export { NavDropdownMenu };
export default NavDropdownMenu;
